import { Box } from "@mui/material"
import { AddOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useEffect, useMemo, useRef, useState } from "react"

import { Button } from "@/components/common/Button"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import type { ClassificationListSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { FilterDrawer } from "@/screens/ManageProducts/components/ProductClasses/components/filters/FilterDrawer"
import ProductClassificationsTable from "@/screens/ManageProducts/components/ProductClasses/components/table/productClassificationsTable"
import ClassFormDialog from "@/screens/ManageProducts/components/ProductClasses/dialogs/ClassFormDialog"
import { useProductClassFiltering } from "@/screens/ManageProducts/components/ProductClasses/hooks/useProductClassFiltering"
import useSearchList from "@/screens/ManageProducts/components/ProductClasses/hooks/useSearchList"
import type { DialogMode } from "@/screens/ManageProducts/components/ProductClasses/types"
import { DialogModes } from "@/screens/ManageProducts/components/ProductClasses/types"
import type { ClassificationSortableAccessorKey } from "@/screens/ManageProducts/components/ProductClasses/utils"
import {
  accessorKeyToClassificationSortField,
  classificationSortFieldToAccessoryKey,
} from "@/screens/ManageProducts/components/ProductClasses/utils"
import type { ClassificationsSearchFilters } from "@/screens/ManageProducts/stores/useProductClassesSearchStore"
import { useClassificationsSearchStore } from "@/screens/ManageProducts/stores/useProductClassesSearchStore"

const ProductClassContainer: FC = () => {
  const navigate = useNavigate()
  const { filterDrawerOpen, setFilterDrawerOpen } = useProductClassFiltering()

  const [openDialog, setOpenDialog] = useState<DialogMode | null>(null)

  const { searchParams, refetchData } = useSearchList()
  const { query, field, order } = searchParams

  const data = useClassificationsSearchStore(({ data }) => data)
  const fetching = useClassificationsSearchStore(({ fetching }) => fetching)
  const error = useClassificationsSearchStore(({ error }) => error)
  const loadMore = useClassificationsSearchStore(({ loadMore }) => loadMore)
  const clearStore = useClassificationsSearchStore(({ clearStore }) => clearStore)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  useEffect(() => {
    refetchData(false)
  }, [refetchData])

  const [throttledLoadMore] = useThrottle(() => {
    loadMore(queryClient, searchParams)
  }, 200)

  const scrollHandler = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = classificationSortFieldToAccessoryKey.get(field as ClassificationListSortField)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const handleSortingChange = (updaterFn: (state: SortingState) => SortingState) => {
    const newSorting = updaterFn(sorting)
    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id as ClassificationSortableAccessorKey
      const field = accessorKeyToClassificationSortField.get(key) as ClassificationListSortField
      const params: ClassificationsSearchFilters = {
        query,
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      navigate({
        search: params,
        to: "/app/admin/manage-products/classes",
        replace: true,
      })
    }
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      <TableHeaderActions currentSearch={searchParams} placeholder='Search by Product Category, Class or Manager...'>
        <Button
          appearance='outlined'
          value='Filter'
          startIcon={<FilterAltOutlined />}
          onClick={() => setFilterDrawerOpen(true)}
          rootElementName='button'
        />
        <Button startIcon={<AddOutlined />} appearance='outlined' onClick={() => setOpenDialog(DialogModes.Create)}>
          New Product Class
        </Button>
      </TableHeaderActions>

      <FilterDrawer showFilterDrawer={filterDrawerOpen} toggleDrawer={setFilterDrawerOpen} />

      {/* Table Section */}
      <Box className='flex flex-1 flex-col overflow-y-auto'>
        <ProductClassificationsTable
          data={data}
          tableContainerRef={tableContainerRef}
          onScroll={scrollHandler}
          onSortingChange={handleSortingChange}
          sorting={sorting}
          fetching={fetching}
          error={error}
        />
      </Box>
      {openDialog === DialogModes.Create && (
        <ClassFormDialog onClose={() => setOpenDialog(null)} onSuccess={refetchData} />
      )}
    </Box>
  )
}

export default ProductClassContainer
